import { Card, CardContent, Grid, Skeleton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useListContext } from 'react-admin';
import { DEFAULT_API_ADMIN } from '../../constants';
import { getChainId, httpClientReactAdmin } from '../../lib';

/**
 * Cards de totais da recarga elétrica.
 *
 * Busca separada de propósito: o endpoint devolve `summary` ao lado de `data`, mas o
 * react-admin só guarda `data` e `total` — o resto é descartado antes de chegar na tela. E o
 * somatório precisa cobrir o recorte INTEIRO do filtro, não a página visível, então não dá pra
 * calcular a partir das linhas carregadas.
 *
 * Acompanha os filtros da lista (`filterValues`), senão os cards mostrariam um recorte e a
 * tabela outro — que é pior que não ter card nenhum.
 */

type Summary = {
  sessions: number;
  pointed: number;
  pendingOrExpired: number;
  totalKwh: number;
  totalValue: number;
  totalPoints: number;
  avgKwh: number;
  pointedRate: number;
};

const numberBR = (value: number, digits = 0) =>
  (value ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const Metric = ({ label, value, hint }: { label: string, value: string, hint?: string }) => (
  <Grid size={{ xs: 6, sm: 4, md: 2 }}>
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{value}</Typography>
        {hint ? <Typography variant="caption" color="text.secondary">{hint}</Typography> : null}
      </CardContent>
    </Card>
  </Grid>
);

const ChargeSessionsSummary = () => {
  const { filterValues } = useListContext();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const filterKey = JSON.stringify(filterValues || {});

  useEffect(() => {
    let canceled = false;
    setIsLoading(true);

    const query = new URLSearchParams();
    // `take=1` porque só interessa o `summary`: ele é calculado sobre todo o recorte do
    // filtro no backend, independente da paginação.
    query.set('take', '1');
    if (filterValues && Object.keys(filterValues).length) {
      query.set('filter', JSON.stringify(filterValues));
    }

    const url = `${DEFAULT_API_ADMIN}/chains/${getChainId()}/charge-sessions?${query.toString()}`;

    httpClientReactAdmin(url, { method: 'GET' })
      .then(({ json }) => { if (!canceled) { setSummary(json?.summary ?? null); } })
      .catch(() => { if (!canceled) { setSummary(null); } })
      .finally(() => { if (!canceled) { setIsLoading(false); } });

    return () => { canceled = true; };
  }, [filterKey]);

  if (isLoading) {
    return <Skeleton variant="rounded" height={84} sx={{ mb: 2 }} />;
  }

  if (!summary) { return null; }

  return (
    <Grid container spacing={1} sx={{ mb: 2 }}>
      <Metric label="Sessões" value={numberBR(summary.sessions)} />
      <Metric
        label="Pontuadas"
        value={`${numberBR((summary.pointedRate ?? 0) * 100, 1)}%`}
        hint={`${numberBR(summary.pointed)} de ${numberBR(summary.sessions)}`}
      />
      <Metric label="Sem desfecho" value={numberBR(summary.pendingOrExpired)} hint="aguardando ou expiradas" />
      <Metric label="Energia" value={`${numberBR(summary.totalKwh, 1)} kWh`} hint={`média ${numberBR(summary.avgKwh, 1)} kWh`} />
      <Metric label="Valor" value={`R$ ${numberBR(summary.totalValue, 2)}`} />
      <Metric label="Pontos" value={numberBR(summary.totalPoints, 2)} />
    </Grid>
  );
};

export default ChargeSessionsSummary;
